import React, { useState } from "react";
import { ReactComponent as CorrectIcon } from "assets/icons/correct_icon.svg";
import useDialog from "hooks/useDialog";
import useAuth from "hooks/useAuth";
import useSubscription from "hooks/useSubscription";
import ButtonSpinner from "components/Loading/ButtonSpinner";
import { PLAN_FEATURES } from "utils/constants";

const FeatureItem = ({ text }) => (
  <div className="flex items-center space-x-3">
    <div className="w-6 h-6 rounded-full bg-[#DCFAE6] flex items-center justify-center">
      <CorrectIcon />
    </div>
    <p className="text-[16px] font-normal leading-[24px] text-[#000000A6]">
      {text}
    </p>
  </div>
);

const PlanCard = ({
  loading,
  title,
  description,
  price,
  features,
  buttonText,
  showToggle,
  isQuarterly,
  onToggle,
  onSubscribe,
  planType,
  loadingPlan,
  isSubscribed,
}) => (
  <div className="border w-full max-w-[650px] border-[#F0F0F0] rounded-[16px] signin-free-box-shadow">
    <div
      className={`${
        showToggle ? "h-auto sm:h-[195px]" : "h-auto sm:h-[137px]"
      } border-b border-[#F0F0F0] p-4 sm:p-6`}
    >
      <div className="flex  sm:items-center sm:space-x-8 space-y-4 sm:space-y-0 flex-col sm:flex-row sm:justify-between">
        <div className="">
          <h3 className="text-[24px] leading-[32px] font-semibold text-black">
            {title}
          </h3>
          <p
            style={{ height: showToggle ? 72 : "auto" }}
            className="text-[14px] leading-[24px] font-normal mt-1 text-[#000000A6]"
          >
            {description}
          </p>
        </div>

        {typeof price === "string" ? (
          <h2 className="text-[36px] font-semibold text-primary leading-[72px]">
            {price}
          </h2>
        ) : (
          <div className="flex items-start space-x-[2px]">
            <h2 className="text-[36px] font-semibold text-primary leading-[72px]">
              €{isQuarterly ? "9,90" : price.amount}
            </h2>
            <p className="text-[12px] text-nowrap leading-[24px] font-medium mt-[37px] text-[#00000082]">
              {price.period}
            </p>
          </div>
        )}
      </div>
      {showToggle && (
        <div className="flex items-center space-x-3 mt-4">
          <h2 className="text-[13px] sm:text-[18px] leading-[28px] text-[#727070] font-normal">
            Mensile
          </h2>
          <div>
            <input
              checked={isQuarterly}
              onChange={onToggle}
              type="checkbox"
              name="switch"
              id="switch"
              className="checkbox_input hidden"
            />
            <label
              className="checkbox_label !bg-[#0011FF] !w-[35px] after:!top-[2px] after:w-[15px] after:h-[15px] cursor-pointer"
              htmlFor="switch"
            ></label>
          </div>
          <h2 className="text-[13px] sm:text-[18px] leading-[28px] text-[#182230] font-normal">
            Semestrale (Risparmia il 50%)
          </h2>
        </div>
      )}
    </div>
    <div
      className={`${
        showToggle ? "h-[474px] sm:h-[301px]" : "h-[290px] sm:h-[359px]"
      } border-b border-[#EAECF0] p-4 sm:p-6`}
    >
      <div className="grid items-center justify-between gap-x-8 gap-y-4 sm:grid-cols-2">
        {features.map((feature, index) => (
          <FeatureItem key={index} text={feature} />
        ))}
      </div>
    </div>
    <div className={`${showToggle ? "h-[102px] sm:h-[88px]" : "h-[88px]"} p-6`}>
      <button
        className={`bg-primary text-white w-full h-10 flex items-center justify-center rounded-[8px] text-[16px] font-semibold leading-[30px] ${
          isSubscribed ? "opacity-50 cursor-not-allowed" : ""
        }`}
        onClick={onSubscribe}
        disabled={
          (loading.isCreatingSubscription && loadingPlan === planType) ||
          isSubscribed
        }
      >
        {loading.isCreatingSubscription && loadingPlan === planType ? (
          <ButtonSpinner
            style={{
              borderWidth: "3px",
              width: "19px",
              height: "19px",
            }}
          />
        ) : (
          buttonText
        )}
      </button>
      {showToggle && (
        <p className="text-[13px] sm:hidden font-normal text-[#00000099] leading-[18px] text-center mt-3">
          Prezzo Mensile{" "}
          <strong className="text-black">
            {isQuarterly ? "€9,90" : `€${price.amount}`}
          </strong>
        </p>
      )}
    </div>
  </div>
);

const SignInFeeSection = () => {
  const { openSigninDialog } = useDialog();
  const { user } = useAuth();
  const { subscribe, loading, subscriptionStatus } = useSubscription();
  const [isQuarterly, setIsQuarterly] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleToggleQuarterly = (e) => {
    setIsQuarterly(e.target.checked);
  };

  const handleSubscribe = (planType) => {
    if (!user) {
      // If not logged in, open signup dialog first
      openSigninDialog();
      return;
    }

    setLoadingPlan(planType);

    // Get the appropriate plan ID based on the plan type
    let planId;
    if (planType === "free") {
      return;
    } else if (planType === "premium") {
      // Set the correct plan ID based on billing frequency
      planId = isQuarterly
        ? process.env.REACT_APP_STRIPE_PRICE_PREMIUM_QUARTERLY
        : process.env.REACT_APP_STRIPE_PRICE_PREMIUM;
    }

    if (planId) {
      subscribe(
        planId,
        `${window.location.origin}/settings?tab=subscription&status=success`,
        `${window.location.origin}?status=cancel`,
        isQuarterly
      );
    }
  };

  return (
    <div
      id="sign-in-fee-section"
      className="mt-10 mb-20 sm:mb-[112px] lg:pt-[103px] px-5"
    >
      <div className="max-w-[1320px] mx-auto">
        <div className="text-center">
          <h2 className="text-black text-center font-bold text-[30px] lg:text-[56px] w-full leading-[1.3] md:leading-[40px] lg:leading-[67.77px]">
            Iscriviti <span className="text-primary">gratuitamente</span> su
            HouseScan!
          </h2>
          <div className="lg:max-w-[760px] mx-auto w-full mt-[15px] lg:mt-5">
            <p className="text-[13px] font-normal text-black leading-[21px] ">
              Scegli il piano più adatto alle tue esigenze e inizia subito la
              tua ricerca!
            </p>
          </div>
        </div>
        <div className="mt-12 max-w-[1320px] mx-auto flex items-center flex-col xl:flex-row space-y-5 xl:space-y-0 xl:space-x-5">
          <PlanCard
            loading={loading}
            title="Basic"
            description="Non sei sicuro di un abbonamento? Inizia GRATIS! Organizza le tue ricerche da tutti i portali con il nostro organizer evoluto"
            price="Gratis"
            features={PLAN_FEATURES.free}
            buttonText={
              subscriptionStatus.hasPremium || user ? "Iscritto" : "Iscriviti"
            }
            onSubscribe={() => handleSubscribe("free")}
            planType="free"
            loadingPlan={loadingPlan}
            isSubscribed={subscriptionStatus.hasPremium || user}
          />
          <PlanCard
            loading={loading}
            title={!isQuarterly ? "Pro Mensile" : "Pro Semestrale"}
            description={
              !isQuarterly
                ? "IL 38% di chi cerca casa la trova entro un mese! Basta una tantum e in un mese avrai a disposizione uno strumento che ti farà risparmiare tempo e denaro!"
                : "Il nostro piano più popolare! Flessibilità totale e ricerca senza pensieri, con tutti i vantaggi Premium"
            }
            price={{
              amount: "19,90",
              period: "/al mese",
            }}
            features={PLAN_FEATURES.premium}
            buttonText={
              subscriptionStatus.hasPremium ? "Iscritto" : "Acquista Ora"
            }
            showToggle={true}
            isQuarterly={isQuarterly}
            onToggle={handleToggleQuarterly}
            onSubscribe={() => handleSubscribe("premium")}
            planType="premium"
            loadingPlan={loadingPlan}
            isSubscribed={subscriptionStatus.hasPremium}
          />
        </div>
      </div>
    </div>
  );
};

export default SignInFeeSection;
